import { PaymentMethod } from './payments.types';
import { PageInfo } from './pageInfo.types';
import {
  Ad,
  AssetUrl,
  MutationResponse,
  PlatformSource,
  Shop,
} from './common.types';
import { AdService } from './services.types';
import { AdStatus } from '@/types';
import { Region } from '@/api/types/regions.types';
import { Category, PropertyType } from '@/api/types/categories.types';

export enum ServiceName {
  Vip = 'VIPPED',
  Premium = 'PREMIUM',
  Latest = 'LATEST',
}

export enum AdOrder {
  DateDesc = 'DATE_DESC', // by default
  PriceAsc = 'PRICE_ASC',
  PriceDesc = 'PRICE_DESC',
  Random = 'RANDOM',
}

export enum ContactType {
  CallsAndMessages = 'CALLS_AND_MESSAGES',
  OnlyCalls = 'ONLY_CALLS',
  OnlyMessages = 'ONLY_MESSAGES',
}

export enum AdSubmissionStatusEnum {
  Start = 'START',
  CategorySelect = 'CATEGORY_SELECT',
  Cancelled = 'CANCELLED',
  Reload = 'RELOAD',
}

export type AdContact = {
  id: string;
  name: string;
  logo: AssetUrl | null;
  phones: string[];
  type: ContactType;
};

export type AdPendingEdit = {
  body: string;
  contactType: ContactType;
  photos: AssetUrl[];
  price: number | null;
  properties: AdProperty[];
};

export type AdProperty = {
  legacyResourceId: string;
  name: string;
  value: string;
  originalValue: {
    value: boolean | string | number;
  };
  type: PropertyType;
  link: string | null;
};

export type ReportReason = {
  id: string;
  name: string;
};

export type DeleteReason = {
  id: string;
  name: string;
  descriptionRequired: boolean;
};

export type RejectReason = {
  id: string;
  template: string;
};

export type AdDetails = Omit<Ad, 'shop'> & {
  photo: never;
  photos: AssetUrl[];
  photosPreview: AssetUrl[];
  bookmarksCount: number;
  callsCount: number;
  hits: number;
  shop: Shop | null;
  category?: Category;
  availableTemplatePhotos?: AssetUrl[];
  contact: AdContact;
  status: AdStatus;
  properties: AdProperty[];
  azProperties: AdProperty[];
  azRegion: string;
  legacyResourceId: number;
  isBookmarked: boolean;
  birkartBanner: {
    hasCard: boolean;
    hasModal: boolean;
    hasKredit: boolean;
  };
  body: string;
  relatedAds: Ad[];
  user: {
    id: string;
    legacyId: number;
    publishedAdsCount: number;
    isBlockedMe: boolean;
    isBlockedByMe: boolean;
  };
  reportReasons: ReportReason[];
  deleteReasons: DeleteReason[];
  expiresAt: string | null;
  chatPath: string;
  vippedUntil: string | null;
  featuredUntil: string | null;
  bumpServices: AdService[];
  featuredServices: AdService[];
  vipServices: AdService[];
  rejectReasons: RejectReason[] | null;
  rejectReason: string | null;
  isCallsAvailable: boolean;
  isChatAvailable: boolean;
  editLockedUntil: string | null;
  isExpiredManually: boolean;
  isPayFromBundleAvailable: boolean;
  isProlongFree: boolean;
  paymentMethods: PaymentMethod[];
  pendingEdit: AdPendingEdit | null;
  isAppleGooglePayEnabled: boolean;
  hasEditPendingApproval: boolean;
};

export type GetAdsResponse = {
  ads: {
    nodes: Ad[];
    pageInfo: PageInfo;
    totalCount: number;
  };
};

export type GetAdsForBookmarksResponse = {
  ads: {
    nodes: Ad[];
  };
};

export type GetAdDetailsResponse = {
  adDetails: AdDetails;
};

export type CheckAdExistenceResponse = {
  adExistence: string;
};

export type ProlongAdResponse = {
  prolongAd: MutationResponse<boolean>;
};

export type PayForAdByPrepaidBundleResponse = {
  payForAdByPrepaidBundle: MutationResponse<boolean>;
};

export type CreateBookmarkResponse = {
  createBookmark: MutationResponse<boolean>;
};

export type RemoveBookmarkResponse = {
  destroyBookmark: MutationResponse<boolean>;
};

export type CreateAdResponse = {
  createAd: MutationResponse<Pick<Ad, 'id' | 'status' | 'path'>>;
};

export type UpdateAdResponse = {
  updateAd: MutationResponse<Pick<Ad, 'id' | 'status' | 'path'>>;
};

export type CreateAdSubmissionResponse = {
  createAdSubmission: MutationResponse<string>;
};

export type CreateAdAttributes = {
  body: string;
  contactAttributes: {
    contactType: ContactType;
    email: string;
    name: string;
    phones: [string];
  };
  photoIds: string[];
  price: number | null;
  propertySet: {
    collection: { legacyId: string; value: string }[];
    boolean: { legacyId: string; value: boolean }[];
    range: { legacyId: string; value: number }[];
  };
  source: PlatformSource;
  title: string;
  categoryId: string;
  regionId: string;
  flowId?: string;
};

export interface NewAdDefaultValues {
  categories?: Category[];
  defaultRegion?: Region;
  defaultCategory?: Category | null;
  defaultSubcategory?: Category | null;
}

export type UpdateAdAttributes = {
  price: number | null;
  propertySet: {
    boolean: { legacyId: string; value: boolean }[];
  };
  body: string;
  contactType: ContactType;
  photoIds: string[];
};

export type PopularRequest = { page: string; term: string; url: string };

export type GetPopularRequestsResponse = {
  popularRequests: PopularRequest[];
};
